import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import {
    BookRidePlan,
    BookRideSpecification,
    CustomerFacade,
    NotificationFacade,
    RidePlannerFacade,
    ShoppingCartFacade
} from '@ridango/components';
import { BaseComponent } from '@ridango/core';

import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { clearCartState, loadCartState } from '../../../localStorage';
import Env from '@ridango/core/env';

@Component({
    templateUrl: './book-ride-review-page.component.html',
    styleUrls: ['./book-ride-review-page.component.scss']
})
export class BookRideReviewPageComponent extends BaseComponent implements OnInit, OnDestroy {
    profile$: Observable<any> = this.customerFacade.profile$;
    passengerPermission$: Observable<boolean> = this.customerFacade.passengerPermission$;
    cart$: Observable<any> = this.cartFacade.cart$;

    plan: BookRidePlan;
    specification: BookRideSpecification;
    estimatedPrice: number;
    cartId: number;

    locale = this.translate.currentLang;

    showNavigationModal = false;
    navigateAwaySelection$: Subject<boolean> = new Subject<boolean>();

    dateFormat = this.env.TICKET_CARD_DATE_FORMAT;

    bookingActive = false;

    modalTranslations = {
        title: 'BOOK_RIDE_NAVIGATION_MODAL_TITLE',
        text: 'BOOK_RIDE_NAVIGATION_MODAL_TEXT',
        textBold: 'BOOK_RIDE_NAVIGATION_MODAL_TEXT_BOLD',
        allowNavigation: 'BOOK_RIDE_ALLOW_NAVIGATION',
        cancel: 'BOOK_RIDE_ABOUT_TO_END_MODAL_CANCEL'
    };

    constructor(
        @Inject('ENV') public env: Env,
        private ridePlanner: RidePlannerFacade,
        private customerFacade: CustomerFacade,
        private router: Router,
        private translate: TranslateService,
        private notification: NotificationFacade,
        private cartFacade: ShoppingCartFacade
    ) {
        super();
    }

    ngOnInit() {
        this.ridePlanner.step$.pipe(take(1)).subscribe((step) => {
            if (step !== 3) {
                this.router.navigate([this.translate.currentLang, 'book-a-ride']);
            }
        });
        this.ridePlanner.plan$.pipe(take(1)).subscribe((plan) => (this.plan = plan));
        this.ridePlanner.specification$
            .pipe(take(1))
            .subscribe((specification) => (this.specification = specification));
        this.ridePlanner.estimatedPrice$.pipe(take(1)).subscribe((estimatedPrice) => {
            this.estimatedPrice = estimatedPrice;
            this.cartFacade.updateCartTotalPrice({ total: estimatedPrice, total_sum: estimatedPrice });
        });

        this.cart$.pipe(takeUntil(this.componentDestroyed$)).subscribe((c) => {
            this.cartId = c.shopping_cart_id;
        });

        this.ridePlanner.bookingStatus$.pipe(take(1)).subscribe((status) => {
            if (status === 'pending') {
                this.bookingActive = true;
            }
        });

        const loadCart = new Promise((resolve, reject) => {
            const t = loadCartState();
            if (t) {
                const c = Object.assign({}, JSON.parse(JSON.stringify(t)));
                if (c) {
                    resolve(c);
                } else {
                    reject();
                }
            } else {
                reject();
            }
        });

        loadCart.then((c) => {
            if (!this.cartId) {
                this.cartFacade.hydrateState(Object.assign({}, JSON.parse(JSON.stringify(c))));
            }
            clearCartState();
        });
    }

    bookAnotherRide() {
        this.ridePlanner.setIsReturn(false);
        this.router.navigate([this.translate.currentLang, 'book-a-ride']);
    }

    bookReturnRide() {
        this.ridePlanner.setIsReturn(true);
        this.router.navigate([this.translate.currentLang, 'book-a-ride']);
    }

    canDeactivate(): Observable<boolean> | boolean {
        return this.confirm();
    }

    confirm(): Observable<boolean> | boolean {
        if (this.bookingActive && this.cartId) {
            this.showNavigationModal = true;
            return this.navigateAwaySelection$;
        }
        return true;
    }

    onCloseNavigationModal(): void {
        this.showNavigationModal = false;
        this.navigateAwaySelection$.next(false);
    }

    onConfirmNavigationModal(): void {
        this.cartFacade.deleteCart();
        this.navigateAwaySelection$.next(true);
        this.showNavigationModal = false;
    }
}
